import React, { useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, message } from 'antd';
import dayjs from 'dayjs';
import { maximoApi, Team } from '../lib/maximoApi';

const { TextArea } = Input;

interface CreateWorkOrderFormProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  teams: Team[];
}

const CreateWorkOrderForm: React.FC<CreateWorkOrderFormProps> = ({ 
  visible, 
  onClose, 
  onSuccess,
  teams 
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      // Generate a unique work order ID
      const workOrderId = `WO${Date.now().toString().slice(-8)}`;
      
      const workOrderData = {
        id: workOrderId,
        description: values.description,
        priority: values.priority,
        workType: values.workType,
        location: values.location,
        reportDate: values.reportDate.toISOString(),
        status: 'WAPPR', // Waiting for approval
        assignedTeam: values.assignedTeam || undefined
      };

      // Call API to create work order
      await maximoApi.createWorkOrder(workOrderData);
      
      message.success('Work order created successfully');
      form.resetFields();
      onSuccess();
    } catch (error) {
      console.error('Error creating work order:', error);
      message.error('Failed to create work order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Create New Work Order"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      style={{ zIndex: 1050 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          priority: 3,
          workType: 'CM',
          reportDate: dayjs()
        }}
      >
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Please enter work order description' }]}
        >
          <TextArea rows={3} placeholder="Enter work order description" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Please select priority' }]}
          >
            <Select 
              placeholder="Select priority"
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              dropdownStyle={{ zIndex: 1060 }}
            >
              <Select.Option value={1}>P1 - Critical</Select.Option>
              <Select.Option value={2}>P2 - High</Select.Option>
              <Select.Option value={3}>P3 - Medium</Select.Option>
              <Select.Option value={4}>P4 - Low</Select.Option>
              <Select.Option value={5}>P5 - Lowest</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="workType"
            label="Work Type"
            rules={[{ required: true, message: 'Please select work type' }]}
          >
            <Select 
              placeholder="Select work type"
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
              dropdownStyle={{ zIndex: 1060 }}
            >
              <Select.Option value="CM">CM - Corrective Maintenance</Select.Option>
              <Select.Option value="PM">PM - Preventive Maintenance</Select.Option>
              <Select.Option value="PROJECT">Project</Select.Option>
            </Select>
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="location"
            label="Location"
          >
            <Input placeholder="Enter location (optional)" />
          </Form.Item>

          <Form.Item
            name="reportDate"
            label="Report Date"
            rules={[{ required: true, message: 'Please select report date' }]}
          >
            <DatePicker className="w-full" />
          </Form.Item>
        </div>

        <Form.Item
          name="assignedTeam"
          label="Assign to Team (Optional)"
        >
          <Select 
            placeholder="Select team to assign" 
            allowClear
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            dropdownStyle={{ zIndex: 1060 }}
          >
            {teams.map(team => (
              <Select.Option key={team.id} value={team.id}>
                {team.name.replace(/-TEAM$/, '').replace(/-/g, ' ')}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item className="mb-0 flex justify-end">
          <Button onClick={handleCancel} className="mr-2">
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Create Work Order
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateWorkOrderForm;